import {
	Button,
	Card,
	Divider,
	Progress,
	Select,
	Space,
	Statistic,
	Typography,
} from "antd";
import { AlertCircle, CheckCircle, HelpCircle } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { FixingLoader, FixingOverlay } from "@/components/ui/FixingLoader";
import { useApplyFAQSchema } from "@/hooks/useSchemaManagement";
import type { FAQSchemaStatus } from "@/types/schema";

const { Text } = Typography;

interface FAQSchemaCardProps {
	stats?: FAQSchemaStatus;
	onSelectItems?: (contentType: FAQContentType) => void;
}

export const FAQSchemaCard: React.FC<FAQSchemaCardProps> = ({
	stats,
	onSelectItems,
}) => {
	const [contentType, setContentType] = useState<"post" | "service_page">(
		"post",
	);
	const applyFAQSchema = useApplyFAQSchema();

	if (!stats) {
		return (
			<Card loading>
				<div style={{ height: 200 }} />
			</Card>
		);
	}

	const isRunning = applyFAQSchema.isPending;
	const progressPercent =
		stats.total_applicable > 0
			? Math.round((stats.with_schema / stats.total_applicable) * 100)
			: 0;
	const isComplete = stats.without_schema === 0;

	return (
		<Card
			title={
				<Space>
					<HelpCircle size={20} />
					<span>FAQ Schema</span>
				</Space>
			}
			extra={
				isComplete ? (
					<CheckCircle size={20} style={{ color: "#52c41a" }} />
				) : (
					<AlertCircle size={20} style={{ color: "#faad14" }} />
				)
			}
		>
			<div className="relative">
			<FixingOverlay
				show={applyFAQSchema.isPending}
				label="Applying FAQ Schema…"
			/>
			<Space direction="vertical" size={16} style={{ width: "100%" }}>
				{/* Progress Bar */}
				<div>
					<div
						style={{
							marginBottom: 8,
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Text type="secondary">Implementation Progress</Text>
						<Text strong>{progressPercent}%</Text>
					</div>
					<Progress
						percent={progressPercent}
						status={isComplete ? "success" : "active"}
						strokeColor={isComplete ? "#52c41a" : "#1677ff"}
					/>
				</div>

				{/* Statistics */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr 1fr",
						gap: 16,
					}}
				>
					<Statistic
						title="Total Items"
						value={stats.total_applicable}
						valueStyle={{ fontSize: 20 }}
					/>
					<Statistic
						title="With FAQ"
						value={stats.with_schema}
						valueStyle={{ fontSize: 20, color: "#52c41a" }}
					/>
					<Statistic
						title="Needing FAQ"
						value={stats.without_schema}
						valueStyle={{
							fontSize: 20,
							color: stats.without_schema > 0 ? "#faad14" : "#52c41a",
						}}
					/>
				</div>

				<Divider style={{ margin: 0 }} />

				{/* Content Type Selector */}
				<div>
					<Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
						Generate FAQs for
					</Text>
					<Select
						size="large"
						style={{ width: "100%" }}
						value={contentType}
						onChange={setContentType}
						options={[
							{ label: "Blog Posts", value: "post" },
							{ label: "Service Pages", value: "service_page" },
						]}
					/>
				</div>

				{/* Action Buttons */}
				<Button
					type="primary"
					block
					size="large"
					onClick={() => applyFAQSchema.mutate(contentType)}
					disabled={isRunning || isComplete}
					icon={
						isRunning ? (
							<FixingLoader iconOnly label="Applying FAQ Schema…" />
						) : undefined
					}
				>
					{isRunning
						? "Generating FAQs..."
						: isComplete
							? "All Items Have FAQs"
							: "Generate FAQs"}
				</Button>

				{onSelectItems && (
					<Button
						block
						size="large"
						onClick={() => onSelectItems(contentType)}
						disabled={isRunning}
					>
						Select Specific Items
					</Button>
				)}

				{/* Help Text */}
				<Text type="secondary" style={{ fontSize: 12 }}>
					FAQ schema uses AI to generate frequently asked questions based on
					your content, improving search engine visibility and rich results.
				</Text>
			</Space>
			</div>
		</Card>
	);
};

export default FAQSchemaCard;
